// PayStructureMaster.js
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";

export default function PayStructureMaster() {
  const [open, setOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  // Master list
  const [structures, setStructures] = useState([
    { name: "Below 30K - Male", gender: "Male", range: "< 30,000", active: true },
    { name: "Below 30K - Female", gender: "Female", range: "< 30,000", active: true },
    { name: "Above 30K", gender: "Both", range: ">= 30,000", active: true },
  ]);

  // Mapping details per structure (dummy for now)
  const [mappings, setMappings] = useState({
    "Below 30K - Male": [
      { component: "Basic", method: "% of Gross", value: "40%", monthly: "11632", annual: "139584" },
      { component: "DA", method: "% of Basic", value: "20%", monthly: "3614", annual: "43368" },
      { component: "HRA", method: "% of Basic", value: "20%", monthly: "4800", annual: "57600" },
      { component: "PF", method: "% of Basic", value: "12%", monthly: "1395", annual: "16740" },
    ],
    "Above 30K": [
      { component: "Basic", method: "% of Gross", value: "50%", monthly: "20000", annual: "240000" },
      { component: "HRA", method: "% of Basic", value: "20%", monthly: "8000", annual: "96000" },
    ],
  });

  const [form, setForm] = useState({ name: "", gender: "Both", range: "", active: true });
  const [selectedStructure, setSelectedStructure] = useState(null);

  const handleSave = () => {
    if (editIndex !== null) {
      const updated = [...structures];
      updated[editIndex] = form;
      setStructures(updated);
    } else {
      setStructures([...structures, form]);
    }
    setForm({ name: "", gender: "Both", range: "", active: true });
    setEditIndex(null);
    setOpen(false);
  };

  const handleEdit = (index) => {
    setForm(structures[index]);
    setEditIndex(index);
    setOpen(true);
  };

  const handleDelete = (index) => {
    setStructures(structures.filter((_, i) => i !== index));
  };

  const handleMapping = (structure) => {
    setSelectedStructure(structure);
    setMapOpen(true);
  };

  return (
    <div className="bg-white dark:bg-gray-900 p-4 font-poppins rounded-2xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Pay Structure Master</h2>
        <button
          onClick={() => setOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700"
        >
          + Add Pay Structure
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Structure Name</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Gender</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Salary Range</th>
              <th className="py-2 px-4 text-left  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Active</th>
              <th className="py-2 px-4 text-center  font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {structures.map((s, i) => (
              <tr key={i} className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-950 dark:hover:text-white">
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{s.name}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{s.gender}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{s.range}</td>
                <td className="py-2 px-4 text-xs dark:text-white text-left truncate md:text-sm whitespace-nowrap">{s.active ? "✓" : "✗"}</td>
                <td className="px-4 py-2 flex justify-center items-center gap-2">
                  <button
                    onClick={() => handleEdit(i)}
                    className="px-2 py-1 text-blue-500 hover:underline hover:underline-offset-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleMapping(s.name)}
                    className="px-2 py-1 text-blue-500 hover:underline hover:underline-offset-4"
                  >
                    Mapping
                  </button>
                  <button
                    onClick={() => handleDelete(i)}
                    className="px-2 py-1 text-red-600 hover:underline hover:underline-offset-4"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setOpen(false)}>
          <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/25">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
              <Dialog.Title className="text-lg font-medium text-gray-900">
                {editIndex !== null ? "Edit Pay Structure" : "Add Pay Structure"}
              </Dialog.Title>

              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  placeholder="Structure Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border p-2 rounded"
                />

                <select
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full border p-2 rounded"
                >
                  <option>Male</option>
                  <option>Female</option>
                  <option>Both</option>
                </select>

                <input
                  type="text"
                  placeholder="Salary Range (e.g. < 30,000)"
                  value={form.range}
                  onChange={(e) => setForm({ ...form, range: e.target.value })}
                  className="w-full border p-2 rounded"
                />

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 rounded-md border bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white"
                >
                  Save
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>

      {/* Mapping Modal */}
      <Transition appear show={mapOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setMapOpen(false)}>
          <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/25">
            <Dialog.Panel className="w-full max-w-3xl rounded-lg bg-white p-6 shadow-lg">
              <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                Structure Mapping – {selectedStructure}
              </Dialog.Title>

              <table className="min-w-full border border-gray-200 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 border">Component</th>
                    <th className="px-4 py-2 border">Method</th>
                    <th className="px-4 py-2 border">Value/Formula</th>
                    <th className="px-4 py-2 border">Monthly Amt</th>
                    <th className="px-4 py-2 border">Annual Amt</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedStructure &&
                    mappings[selectedStructure]?.map((m, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 border">{m.component}</td>
                        <td className="px-4 py-2 border">{m.method}</td>
                        <td className="px-4 py-2 border">{m.value}</td>
                        <td className="px-4 py-2 border">{m.monthly}</td>
                        <td className="px-4 py-2 border">{m.annual}</td>
                      </tr>
                    ))}
                </tbody>
              </table>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setMapOpen(false)}
                  className="px-4 py-2 rounded-md bg-blue-600 text-white"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
